import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Transaction } from '@/types/treasury';

// =====================================================
// TYPES
// =====================================================

export type CheckType = 'received' | 'issued';
export type CheckStatus = 'pending' | 'deposited' | 'cleared' | 'bounced' | 'cancelled';

export interface CheckTransaction extends Transaction {
  // Ensure check fields are present
  check_number: string;
  check_date: string;
  check_bank: string;
  check_status: CheckStatus;
  check_drawer_name: string;
}

export interface CheckFilters {
  status?: CheckStatus;
  type?: CheckType;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface DepositCheckData {
  checkId: string;
  depositedDate: string;
  notes?: string;
}

export interface DishonorCheckData {
  checkId: string;
  bounceReason: string;
  bounceFee?: number;
  notes?: string;
}

// =====================================================
// QUERIES
// =====================================================

/**
 * Get all checks (transactions with check payment method)
 */
export function useChecks(companyId: string, filters?: CheckFilters) {
  return useQuery({
    queryKey: ['checks', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('company_id', companyId)
        .eq('payment_method', 'check')
        .is('deleted_at', null)
        .order('check_date', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query.eq('check_status', filters.status);
      }

      if (filters?.type) {
        // received = income, issued = expense
        const transactionType = filters.type === 'received' ? 'income' : 'expense';
        query = query.eq('type', transactionType);
      }

      if (filters?.dateFrom) {
        query = query.gte('check_date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('check_date', filters.dateTo);
      }

      if (filters?.search) {
        query = query.or(
          `check_number.ilike.%${filters.search}%,check_drawer_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data as CheckTransaction[]) || [];
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get single check details
 */
export function useCheck(checkId: string) {
  return useQuery({
    queryKey: ['check', checkId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', checkId)
        .eq('payment_method', 'check')
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      return data as CheckTransaction;
    },
    enabled: !!checkId,
  });
}

/**
 * Get expiring checks (approaching 180-day limit)
 */
export function useExpiringChecks(companyId: string, daysThreshold: number = 30) {
  return useQuery({
    queryKey: ['expiring-checks', companyId, daysThreshold],
    queryFn: async () => {
      const now = new Date();
      const minDate = new Date();
      minDate.setDate(minDate.getDate() - (180 - daysThreshold)); // 150 days ago = 30 days until expiry

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('company_id', companyId)
        .eq('payment_method', 'check')
        .in('check_status', ['pending', 'deposited'])
        .is('deleted_at', null)
        .lte('check_date', minDate.toISOString());

      if (error) throw error;

      // Calculate days remaining for each check
      const checks = (data as CheckTransaction[]).map((check) => {
        const checkDate = new Date(check.check_date);
        const expirationDate = new Date(checkDate);
        expirationDate.setDate(expirationDate.getDate() + 180);
        
        const daysRemaining = Math.floor(
          (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );

        return {
          ...check,
          expirationDate,
          daysRemaining,
        };
      });

      // Sort by days remaining (most urgent first)
      return checks.sort((a, b) => a.daysRemaining - b.daysRemaining);
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get check statistics
 */
export function useCheckStats(companyId: string) {
  const { data: checks = [] } = useChecks(companyId);

  const now = new Date();

  return {
    total: checks.length,
    byStatus: {
      pending: checks.filter((c) => c.check_status === 'pending').length,
      deposited: checks.filter((c) => c.check_status === 'deposited').length,
      cleared: checks.filter((c) => c.check_status === 'cleared').length,
      bounced: checks.filter((c) => c.check_status === 'bounced').length,
      cancelled: checks.filter((c) => c.check_status === 'cancelled').length,
    },
    byType: {
      received: checks.filter((c) => c.type === 'income').length,
      issued: checks.filter((c) => c.type === 'expense').length,
    },
    totalAmount: {
      received: checks
        .filter((c) => c.type === 'income' && c.check_status !== 'bounced' && c.check_status !== 'cancelled')
        .reduce((sum, c) => sum + c.amount, 0),
      issued: checks
        .filter((c) => c.type === 'expense' && c.check_status !== 'cancelled')
        .reduce((sum, c) => sum + c.amount, 0),
    },
    expiring: checks.filter((c) => {
      if (c.check_status !== 'pending' && c.check_status !== 'deposited') return false;
      const checkDate = new Date(c.check_date);
      const daysOld = Math.floor((now.getTime() - checkDate.getTime()) / (1000 * 60 * 60 * 24));
      return daysOld >= 150; // 30 days until expiration
    }).length,
  };
}

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Deposit a check
 */
export function useDepositCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ checkId, depositedDate, notes }: DepositCheckData) => {
      const updateData: any = {
        check_status: 'deposited',
        updated_at: new Date().toISOString(),
      };

      // Add notes to existing notes if provided
      if (notes) {
        const { data: existing } = await supabase
          .from('transactions')
          .select('notes')
          .eq('id', checkId)
          .single();

        updateData.notes = existing?.notes
          ? `${existing.notes}\n\n[${new Date().toLocaleDateString('fr-FR')}] Déposé: ${notes}`
          : `[${new Date().toLocaleDateString('fr-FR')}] Déposé: ${notes}`;
      }

      const { data, error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', checkId)
        .select()
        .single();

      if (error) throw error;
      return data as CheckTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checks'] });
      queryClient.invalidateQueries({ queryKey: ['check'] });
      queryClient.invalidateQueries({ queryKey: ['expiring-checks'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Mark check as cleared
 */
export function useClearCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (checkId: string) => {
      // Get check details
      const { data: check, error: fetchError } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', checkId)
        .single();

      if (fetchError) throw fetchError;

      // Update check status
      const { data, error } = await supabase
        .from('transactions')
        .update({
          check_status: 'cleared',
          is_reconciled: true,
          reconciliation_date: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', checkId)
        .select()
        .single();

      if (error) throw error;

      // Update account balance (add money for received checks, subtract for issued checks)
      const operation = check.type === 'income' ? 'add' : 'subtract';
      const { error: balanceError } = await supabase.rpc('update_account_balance', {
        p_account_id: check.account_id,
        p_amount: check.amount,
        p_operation: operation,
      });

      if (balanceError) {
        console.error('Balance update error:', balanceError);
        // Don't throw - check already marked as cleared
      }

      return data as CheckTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checks'] });
      queryClient.invalidateQueries({ queryKey: ['check'] });
      queryClient.invalidateQueries({ queryKey: ['expiring-checks'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Mark check as dishonored (bounced)
 */
export function useDishonorCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ checkId, bounceReason, bounceFee, notes }: DishonorCheckData) => {
      // Get check details
      const { data: existing, error: fetchError } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', checkId)
        .single();

      if (fetchError) throw fetchError;

      const bounceNote = `[${new Date().toLocaleDateString('fr-FR')}] Chèque rejeté: ${bounceReason}${
        bounceFee ? ` - Frais: ${bounceFee} DA` : ''
      }${notes ? ` - ${notes}` : ''}`;

      const updatedNotes = existing?.notes ? `${existing.notes}\n\n${bounceNote}` : bounceNote;

      // Update check status to bounced
      const { data, error } = await supabase
        .from('transactions')
        .update({
          check_status: 'bounced',
          notes: updatedNotes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', checkId)
        .select()
        .single();

      if (error) throw error;

      // If check was previously cleared or deposited, reverse the account balance
      // Only reverse for RECEIVED checks (income) - for issued checks, money was never added
      if (existing.type === 'income' && existing.check_status === 'cleared') {
        // Remove money that was added when check was cleared
        const { error: balanceError } = await supabase.rpc('update_account_balance', {
          p_account_id: existing.account_id,
          p_amount: existing.amount,
          p_operation: 'subtract',
        });

        if (balanceError) {
          console.error('Balance reversal error:', balanceError);
        }
      }

      // If there's a bounce fee, create a separate expense transaction
      if (bounceFee && bounceFee > 0 && existing) {
        const { error: feeError } = await supabase.from('transactions').insert({
          company_id: existing.company_id,
          account_id: existing.account_id,
          type: 'expense',
          category: 'bank_fees',
          amount: bounceFee,
          currency: 'DZD',
          payment_method: 'other',
          transaction_date: new Date().toISOString(),
          transaction_number: `BOUNCE-FEE-${Date.now()}`,
          description: `Frais de rejet de chèque - ${bounceReason}`,
          notes: `Lié au chèque rejeté: ${checkId}`,
          created_by: existing.created_by,
          is_reconciled: false,
        });

        if (feeError) {
          console.error('Bounce fee transaction error:', feeError);
        }

        // Deduct bounce fee from account
        const { error: feeBalanceError } = await supabase.rpc('update_account_balance', {
          p_account_id: existing.account_id,
          p_amount: bounceFee,
          p_operation: 'subtract',
        });

        if (feeBalanceError) {
          console.error('Bounce fee balance error:', feeBalanceError);
        }
      }

      return data as CheckTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checks'] });
      queryClient.invalidateQueries({ queryKey: ['check'] });
      queryClient.invalidateQueries({ queryKey: ['expiring-checks'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Cancel a check
 */
export function useCancelCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ checkId, reason }: { checkId: string; reason?: string }) => {
      const { data: existing } = await supabase
        .from('transactions')
        .select('notes')
        .eq('id', checkId)
        .single();

      const cancelNote = `[${new Date().toLocaleDateString('fr-FR')}] Chèque annulé${
        reason ? `: ${reason}` : ''
      }`;

      const updatedNotes = existing?.notes ? `${existing.notes}\n\n${cancelNote}` : cancelNote;

      const { data, error } = await supabase
        .from('transactions')
        .update({
          check_status: 'cancelled',
          notes: updatedNotes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', checkId)
        .select()
        .single();

      if (error) throw error;
      return data as CheckTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checks'] });
      queryClient.invalidateQueries({ queryKey: ['check'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Calculate expiration date for a check (180 days from check date)
 */
export function getCheckExpirationDate(checkDate: string): Date {
  const date = new Date(checkDate);
  date.setDate(date.getDate() + 180);
  return date;
}

/**
 * Calculate days remaining until check expires
 */
export function getDaysUntilExpiration(checkDate: string): number {
  const expirationDate = getCheckExpirationDate(checkDate);
  const now = new Date();
  const diffTime = expirationDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Get expiration status color
 */
export function getExpirationStatus(checkDate: string): {
  status: 'safe' | 'watch' | 'urgent' | 'critical' | 'expired';
  color: string;
  bgColor: string;
  label: string;
} {
  const daysRemaining = getDaysUntilExpiration(checkDate);

  if (daysRemaining < 0) {
    return {
      status: 'expired',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      label: 'Expiré',
    };
  }

  if (daysRemaining <= 7) {
    return {
      status: 'critical',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      label: 'Critique',
    };
  }

  if (daysRemaining <= 30) {
    return {
      status: 'urgent',
      color: 'text-orange-700',
      bgColor: 'bg-orange-100',
      label: 'Urgent',
    };
  }

  if (daysRemaining <= 60) {
    return {
      status: 'watch',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
      label: 'À surveiller',
    };
  }

  return {
    status: 'safe',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    label: 'OK',
  };
}

/**
 * Format check status for display
 */
export function getCheckStatusDisplay(status: CheckStatus): {
  label: string;
  labelAr: string;
  color: string;
  bgColor: string;
  icon: string;
} {
  const statusMap = {
    pending: {
      label: 'En attente',
      labelAr: 'قيد الانتظار',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
      icon: '⏳',
    },
    deposited: {
      label: 'Déposé',
      labelAr: 'مودع',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      icon: '🏦',
    },
    cleared: {
      label: 'Encaissé',
      labelAr: 'محصّل',
      color: 'text-green-700',
      bgColor: 'bg-green-100',
      icon: '✅',
    },
    bounced: {
      label: 'Rejeté',
      labelAr: 'مرتد',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      icon: '❌',
    },
    cancelled: {
      label: 'Annulé',
      labelAr: 'ملغى',
      color: 'text-gray-700',
      bgColor: 'bg-gray-100',
      icon: '⛔',
    },
  };

  return statusMap[status];
}
